/**
 * Round 15 A17 -- what a document contains that a reader should be told.
 *
 * The product could list the passages in a document addressed to an
 * automated reader, and said so only on a design-test proposal; the
 * locker, the picker, the covering list and the gap page said nothing.
 * And Word hidden text or white one-point text was extracted and shown
 * as ordinary text. Every surface that names a document renders this,
 * from counts the extraction recorded, so they cannot disagree.
 *
 * Silent when there is nothing to say, and silent when the text was
 * never examined (null): "none found" would claim a look that did not
 * happen.
 */
import { useTranslation } from "react-i18next";

export interface DocumentReading {
  directed_passages?: number | null;
  hidden_passages?: number | null;
}

export function hasReading(r: DocumentReading): boolean {
  return (r.directed_passages ?? 0) > 0 || (r.hidden_passages ?? 0) > 0;
}

export function DocumentReadingNote({
  reading,
  testId,
  subject = "document",
}: {
  reading: DocumentReading;
  testId?: string;
  /** "document" for one file; "documents" for a set read together. */
  subject?: "document" | "documents";
}) {
  const { t } = useTranslation();
  const directed = reading.directed_passages ?? 0;
  const hidden = reading.hidden_passages ?? 0;
  if (directed === 0 && hidden === 0) return null;
  return (
    <span
      data-testid={testId}
      data-directed={directed}
      data-hidden={hidden}
      className="block text-[11px] font-medium text-amber-900"
    >
      {directed > 0 && t(`documents.reading.${subject}.directed`, { count: directed })}
      {directed > 0 && hidden > 0 && " "}
      {hidden > 0 &&
        t(`documents.reading.${subject}.${directed > 0 ? "hidden" : "hiddenAlone"}`, {
          count: hidden,
        })}
    </span>
  );
}

/** A set of documents read together, e.g. what a design test would read. */
export function sumReadings(rs: DocumentReading[]): DocumentReading {
  return {
    directed_passages: rs.reduce((n, r) => n + (r.directed_passages ?? 0), 0),
    hidden_passages: rs.reduce((n, r) => n + (r.hidden_passages ?? 0), 0),
  };
}

/**
 * Is this line of extracted text one the original did not show? A hidden
 * passage is one paragraph's invisible runs; its line may carry visible
 * text beside it, and a PDF passage may span lines, so either containing
 * the other is a match.
 */
export function isHiddenLine(line: string, hidden: string[]): boolean {
  const l = line.split(/\s+/).join(" ").trim();
  if (l.length < 3) return false;
  return hidden.some((h) => l.includes(h) || h.includes(l));
}
